/**
 * ChipCancellationHistoryDialog - Linha do tempo de um lote ou protocolo de cancelamento.
 * Fonte: `get_stock_chip_cancellation_events` (eventos imutáveis).
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CANCEL_EVENT_LABEL, useChipCancelEvents, type CancelEvent } from '@/hooks/useStockChipCancellations';

const EXC: Record<string, string> = { keep: 'deixadas pendentes', reject: 'recusadas' };

/** Resumo legível dos detalhes do evento. */
export function describeCancelEvent(e: CancelEvent): string {
  const d = e.details ?? {};
  const parts: string[] = [];
  if (d.protocol_number) parts.push(`Protocolo ${d.protocol_number}`);
  if (typeof d.items === 'number') parts.push(`${d.items.toLocaleString('pt-BR')} chip(s)`);
  if (typeof d.removed === 'number' && d.removed > 0) parts.push(`${d.removed} retirado(s)`);
  if (typeof d.exceptions === 'number' && d.exceptions > 0) parts.push(`${d.exceptions} exceção(ões) ${EXC[String(d.exception_action)] ?? ''}`.trim());
  if (d.date) parts.push(`Data ${new Date(`${d.date}T12:00:00`).toLocaleDateString('pt-BR')}`);
  if (d.reason) parts.push(`Motivo: ${d.reason}`);
  return parts.join(' · ');
}

export function ChipCancellationHistoryDialog({ target, title, onOpenChange }: {
  target: { batchId?: string | null; protocolId?: string | null } | null; title: string; onOpenChange: (o: boolean) => void;
}) {
  return <Dialog open={!!target} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-lg">
      <DialogHeader><DialogTitle>Histórico</DialogTitle><DialogDescription>{title}</DialogDescription></DialogHeader>
      <ChipCancellationHistoryContent target={target} />
    </DialogContent>
  </Dialog>;
}

/** Conteúdo único da timeline, consultado somente quando a aba estiver montada. */
export function ChipCancellationHistoryContent({ target }: { target: { batchId?: string | null; protocolId?: string | null } | null }) {
  const { data = [], isLoading, error } = useChipCancelEvents(target);
  return <div>{isLoading ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
          : error ? <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
          : data.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum evento registrado.</p>
          : (
            <ol className="relative space-y-4 border-l pl-4">
              {data.map((e) => (
                <li key={e.id} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <p className="text-sm font-medium">{CANCEL_EVENT_LABEL[e.event_type] ?? e.event_type}</p>
                  {describeCancelEvent(e) && <p className="text-xs text-muted-foreground">{describeCancelEvent(e)}</p>}
                  <p className="text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleString('pt-BR')}{e.created_by_name ? ` · ${e.created_by_name}` : ''}</p>
                </li>
              ))}
            </ol>
          )}</div>;
}
